import { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { getKlines, fmtUsd } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useTheme } from "@/contexts/ThemeContext";

const RANGES = [
  { label: "1G", days: "1" },
  { label: "7G", days: "7" },
  { label: "30G", days: "30" },
];

export const PriceChart = ({ symbol, price, change24h }) => {
  const { isLight } = useTheme();
  const [data, setData] = useState([]);
  const [days, setDays] = useState("1");
  const [loading, setLoading] = useState(true);
  const up = (change24h ?? 0) >= 0;
  const color = up ? (isLight ? "#00B877" : "#00FFA3") : (isLight ? "#E11D48" : "#FF3366");
  const gridStroke = isLight ? "#E4E4E7" : "#1A1A1D";
  const axisText = isLight ? "#A1A1AA" : "#71717A";
  const tipBg = isLight ? "#FFFFFF" : "#0F0F11";
  const tipBorder = isLight ? "#E4E4E7" : "#27272A";

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getKlines(symbol, days)
      .then((d) => { if (alive) setData(d); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [symbol, days]);

  return (
    <div data-testid="price-chart" className="min-w-0 rounded-lg border border-[var(--border)] bg-[var(--surface)] p-5">
      <div className="flex flex-wrap gap-3 items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-head font-bold text-lg">{symbol}<span className="text-[var(--text-faint)] font-normal">/USDT</span></h3>
          </div>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="font-mono text-2xl font-bold">{fmtUsd(price)}</span>
            <span className={cn("font-mono text-sm", up ? "text-[var(--pos)]" : "text-[var(--neg)]")}>
              {up ? "+" : ""}{(change24h ?? 0).toFixed(2)}%
            </span>
          </div>
        </div>
        <div className="flex gap-1 rounded-md border border-[var(--border)] p-1">
          {RANGES.map((r) => (
            <button
              key={r.days}
              data-testid={`chart-range-${r.days}`}
              onClick={() => setDays(r.days)}
              className={cn("px-2.5 py-1 rounded text-xs font-mono transition-colors",
                days === r.days ? "bg-[var(--surface-hover)] text-[var(--text-primary)]" : "text-[var(--text-faint)] hover:text-[var(--text-primary)]")}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div data-testid="price-chart-canvas-container" className="h-[220px] sm:h-[260px] w-full min-w-0 overflow-hidden">
        {loading ? (
          <div className="h-full w-full flex items-center justify-center text-[var(--text-faint)] text-sm animate-pulse">
            Grafik yükleniyor...
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={220}
            initialDimension={{ width: 320, height: 220 }} debounce={1}>
            <AreaChart data={data} margin={{ top: 5, right: 5, left: 5, bottom: 0 }}>
              <defs>
                <linearGradient id="fill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
              <XAxis dataKey="t" hide />
              <YAxis
                domain={["auto", "auto"]}
                orientation="right"
                tick={{ fill: axisText, fontSize: 11, fontFamily: "JetBrains Mono" }}
                tickFormatter={(v) => "$" + Intl.NumberFormat("en", { notation: "compact" }).format(v)}
                axisLine={false}
                tickLine={false}
                width={50}
              />
              <Tooltip
                contentStyle={{ background: tipBg, border: `1px solid ${tipBorder}`, borderRadius: 6, fontFamily: "JetBrains Mono", fontSize: 12, color: "var(--text-primary)" }}
                labelStyle={{ color: axisText }}
                labelFormatter={(t) => new Date(t).toLocaleString()}
                formatter={(v) => [fmtUsd(v), "Fiyat"]}
              />
              <Area type="monotone" dataKey="price" stroke={color} strokeWidth={2} fill="url(#fill)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};
